import { ART_PAL, ball, body, fade, mix, n, shade, tint, vgrad, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Sweet Shop scene behind the game zone (the SVG fallback until the raster scene is there): a cosy candy shop — a warm
 * wall with two soft lamp glows, a candy jar on a small side shelf and lollipops on the left, a cake stand with a
 * cupcake and a stack of macarons (three, never four) on the right, a few sprinkles. The centre stays calm (the reels
 * sit there): only the wall and a soft pool of light. No wallpaper stripes, awnings or counters across the panels.
 * Motifs are marked `data-motif="<x>"` (tests). No filters, no text.
 */
export function sweetShopScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `ss${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;
  const o = (dark: number, light: number) => n(P.dark ? dark : light);

  const wall = P.dark ? mix(P.bg, P.pink, 0.14) : mix(P.pink, '#FFFFFF', 0.86);
  const wallMid = P.dark ? mix(P.bg, P.purple, 0.1) : mix(P.pink, '#FFFFFF', 0.9);
  const floor = P.dark ? mix(P.bg, P.purple, 0.14) : mix(P.purple, '#FFFFFF', 0.8);
  const wood = P.dark ? mix(P.bg, P.orange, 0.32) : mix(P.orange, '#FFFFFF', 0.5);
  const glass = P.dark ? tint(P.sky, 0.3) : P.sky;
  const candyA = P.dark ? P.pink : tint(P.pink, 0.1);
  const candyB = P.dark ? P.teal : tint(P.teal, 0.2);
  const candyC = P.dark ? P.gold : mix(P.gold, '#FFD166', 0.6);
  const cream = P.dark ? mix(P.pink, '#FFFFFF', 0.55) : mix(P.pink, '#FFFFFF', 0.7);

  const defs =
    `<defs>${vgrad(`${p}wl`, wall, wallMid, floor)}` +
    fade(`${p}lm`, P.dark ? P.gold : '#FFFFFF', P.dark ? 0.22 : 0.8) +
    body(`${p}wd`, wood, 0.25, 0.35, 0.2, 1) +
    ball(`${p}ca`, candyA, 0.45, 0.35) +
    ball(`${p}cb`, candyB, 0.45, 0.35) +
    ball(`${p}cc`, candyC, 0.45, 0.35) +
    body(`${p}cr`, cream, 0.35, 0.25) +
    body(`${p}cu`, P.dark ? shade(P.orange, 0.2) : tint(P.orange, 0.3), 0.3, 0.3) +
    fade(`${p}calm`, P.dark ? P.bg : '#FFFFFF', P.dark ? 0.4 : 0.5) +
    `</defs>`;

  /** a lollipop: a spiral disc on a stick */
  const lolly = (x: number, y: number, r: number, g: string, stick: number) =>
    `<g data-motif="${Math.round(x)}"><path d="M${x} ${y}V${y + stick}" stroke="${P.dark ? '#E9E2D6' : '#FFFFFF'}" stroke-width="${n(r * 0.18)}" stroke-linecap="round"/>` +
    `<circle cx="${x}" cy="${y}" r="${r}" fill="url(#${g})"/>` +
    `<path d="M${x} ${y}m${n(-r * 0.3)} 0a${n(r * 0.3)} ${n(r * 0.3)} 0 1 1 ${n(r * 0.6)} 0a${n(r * 0.6)} ${n(r * 0.6)} 0 1 1 ${n(-r * 1.2)} 0" fill="none" stroke="#FFFFFF" stroke-opacity=".55" stroke-width="${n(r * 0.14)}"/></g>`;
  /** a candy jar with round sweets, on a short side shelf */
  const jar = (x: number, y: number, s: number) => {
    const sweets = [
      [-14, 22, 'ca'],
      [12, 24, 'cb'],
      [0, 6, 'cc'],
      [-18, 0, 'cb'],
      [18, 2, 'ca'],
    ]
      .map(([dx, dy, g]) => `<circle cx="${n(x + (dx as number) * s)}" cy="${n(y + (dy as number) * s)}" r="${n(11 * s)}" fill="url(#${p}${g})"/>`)
      .join('');
    return (
      `<g data-motif="${Math.round(x)}"><rect x="${n(x - 70 * s)}" y="${n(y + 40 * s)}" width="${n(140 * s)}" height="${n(12 * s)}" rx="${n(3 * s)}" fill="url(#${p}wd)"/>` +
      sweets +
      `<path d="M${n(x - 36 * s)} ${n(y - 22 * s)}H${n(x + 36 * s)}V${n(y + 30 * s)}Q${n(x + 36 * s)} ${n(y + 40 * s)} ${n(x + 26 * s)} ${n(y + 40 * s)}H${n(x - 26 * s)}Q${n(x - 36 * s)} ${n(y + 40 * s)} ${n(x - 36 * s)} ${n(y + 30 * s)}Z" fill="${glass}" fill-opacity=".16" stroke="${glass}" stroke-opacity=".5" stroke-width="2"/>` +
      `<rect x="${n(x - 30 * s)}" y="${n(y - 34 * s)}" width="${n(60 * s)}" height="${n(12 * s)}" rx="${n(4 * s)}" fill="url(#${p}ca)"/></g>`
    );
  };
  const cupcake = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}"><path d="M${n(x - 26 * s)} ${y}L${n(x - 18 * s)} ${n(y + 34 * s)}H${n(x + 18 * s)}L${n(x + 26 * s)} ${y}Z" fill="url(#${p}cu)"/>` +
    `<path d="M${n(x - 30 * s)} ${y}C${n(x - 36 * s)} ${n(y - 22 * s)} ${n(x - 10 * s)} ${n(y - 22 * s)} ${n(x - 8 * s)} ${n(y - 30 * s)}C${n(x)} ${n(y - 48 * s)} ${n(x + 20 * s)} ${n(y - 34 * s)} ${n(x + 16 * s)} ${n(y - 24 * s)}C${n(x + 36 * s)} ${n(y - 20 * s)} ${n(x + 36 * s)} ${n(y - 2 * s)} ${n(x + 30 * s)} ${y}Z" fill="url(#${p}cr)"/>` +
    `<circle cx="${x}" cy="${n(y - 44 * s)}" r="${n(7 * s)}" fill="${P.red}"/></g>`;
  /** a stand (plate on a foot) */
  const stand = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}" fill="url(#${p}wd)"><rect x="${n(x - 60 * s)}" y="${y}" width="${n(120 * s)}" height="${n(8 * s)}" rx="${n(4 * s)}"/>` +
    `<path d="M${n(x - 6 * s)} ${n(y + 8 * s)}H${n(x + 6 * s)}L${n(x + 16 * s)} ${n(y + 50 * s)}H${n(x - 16 * s)}Z"/></g>`;
  /** three macarons stacked */
  const macarons = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}">` +
    (['cb', 'ca', 'cc'] as const)
      .map(
        (g, i) =>
          `<ellipse cx="${x}" cy="${n(y - i * 22 * s)}" rx="${n(26 * s)}" ry="${n(11 * s)}" fill="url(#${p}${g})"/>` +
          `<rect x="${n(x - 24 * s)}" y="${n(y - i * 22 * s - 2 * s)}" width="${n(48 * s)}" height="${n(4 * s)}" rx="${n(2 * s)}" fill="${cream}"/>`,
      )
      .join('') +
    `</g>`;
  const sprinkles = (pts: number[][]) =>
    pts
      .map(
        ([x, y, a], i) =>
          `<rect x="${x}" y="${y}" width="12" height="4" rx="2" transform="rotate(${a} ${x} ${y})" fill="${[candyA, candyB, candyC][i % 3]}" fill-opacity="${o(0.5, 0.6)}"/>`,
      )
      .join('');

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}wl)"/>` +
    `<ellipse cx="${n(W * 0.18)}" cy="${n(H * 0.18)}" rx="${n(W * 0.18)}" ry="${n(H * 0.26)}" fill="url(#${p}lm)"/>` +
    `<ellipse cx="${n(W * 0.82)}" cy="${n(H * 0.18)}" rx="${n(W * 0.18)}" ry="${n(H * 0.26)}" fill="url(#${p}lm)"/>` +
    `<ellipse cx="${W / 2}" cy="${H * 0.52}" rx="${n(W * 0.38)}" ry="${n(H * 0.42)}" fill="url(#${p}calm)"/>`;

  if (wide) {
    art +=
      jar(270, 330, 1.1) +
      lolly(240, 600, 28, `${p}ca`, 150) +
      lolly(320, 650, 22, `${p}cb`, 110) +
      stand(1320, 440, 1.05) +
      cupcake(1320, 404, 1) +
      macarons(1350, 720, 1.1) +
      lolly(1270, 690, 20, `${p}cc`, 100) +
      sprinkles([
        [420, 220, 30],
        [230, 470, -40],
        [1210, 250, 60],
        [1420, 330, -20],
        [1390, 560, 15],
        [440, 760, 70],
      ]);
  } else {
    art +=
      jar(330, 120, 0.7) +
      lolly(46, 530, 18, `${p}ca`, 90) +
      macarons(350, 580, 0.7) +
      sprinkles([
        [340, 230, 30],
        [30, 420, -40],
        [370, 380, 60],
      ]);
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    defs +
    art +
    `</svg>`
  );
}

/** Header accent of Sweet Shop: a scalloped icing edge with a candy dot (repeat-x tile, 24 × 12). */
export function sweetShopAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const icing = P.dark ? P.pink : tint(P.pink, 0.1);
  const dot = P.teal;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 12" width="24" height="12">` +
    `<path d="M0 0H24V3.5Q18 10 12 3.5Q6 10 0 3.5Z" fill="${icing}" fill-opacity=".75"/>` +
    `<circle cx="6" cy="9.5" r="1.3" fill="${dot}"/>` +
    `</svg>`
  );
}

export default defineScene({ game: 'sweet-shop', scene: sweetShopScene, accent: sweetShopAccent });
